import React from 'react';
import { ArrowRightIcon, CheckIcon, GraduationCapIcon, LoaderIcon, ShieldCheckIcon, SparklesIcon } from 'lucide-react';
import { TCPADisclosure } from './TCPADisclosure';
import { extractListItems, sanitizeTcpaHtml } from '../utils/sanitizeHtml';
import type { NormalizedSchool } from '../utils/schoolFields';

/** Partners send 1–8 bullets; the card must not grow with the verbose ones. */
const MAX_HIGHLIGHTS = 3;
interface SchoolMatchCardProps {
  school: NormalizedSchool;
  variant: 'lead' | 'click';
  /** Lead variant only. */
  consented?: boolean;
  onConsentChange?: (checked: boolean) => void;
  consentError?: string;
  fallbackDisclosure?: string;
  isSubmitting?: boolean;
  onPrimary: () => void;
  onSkip: () => void;
}

/**
 * One school in the match carousel, shared by both phases.
 *
 * Identity resolves first — logo and name on one row — then the reason this
 * school is here, then the pitch. The two variants differ only in that slot and
 * in the action row; everything else is deliberately identical so the two
 * phases cannot drift apart.
 */
export function SchoolMatchCard({
  school,
  variant,
  consented = false,
  onConsentChange,
  consentError,
  fallbackDisclosure,
  isSubmitting = false,
  onPrimary,
  onSkip
}: SchoolMatchCardProps) {
  const isLead = variant === 'lead';
  const highlights = extractListItems(school.bodyHtml).slice(0, MAX_HIGHLIGHTS);
  const disclosureHtml = sanitizeTcpaHtml(school.tcpaHtml);
  // Lead cards explain the program match; click cards use the partner headline.
  const matchLabel = isLead ? 'Your matching program' : 'Why this school';
  const matchValue = isLead ? school.programName : school.headline;
  return <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden" data-id="element-833" data-mp="3cm63hmo85i1z">
      {/* Logo band. White, so partner wordmarks that ship on a white
          background sit flush, with a fixed height so their wildly different
          aspect ratios all occupy the same space. */}
      {school.logo || !isLead && school.highlight ? <div className="relative bg-white border-b border-gray-200 px-6 py-6 flex items-center justify-center min-h-[72px]" data-id="element-834" data-mp="yylu1oe3tfxa">
          {/* On the click card the badge anchors the top-left corner, where it
              reads before the eye reaches the logo. */}
          {!isLead && school.highlight && <span className="absolute top-4 left-4 inline-flex items-center gap-1 rounded-full bg-light border border-gray-200 px-2.5 py-1 text-[11px] font-bold text-navy" data-id="element-835" data-mp="1du1r829zdrxw">
              <SparklesIcon className="w-3.5 h-3.5 text-gold-dark" aria-hidden="true" data-id="element-836" data-mp="l88izd5x1l3q" />
              {school.highlight}
            </span>}
          {school.logo && <img src={school.logo} alt="" className="h-20 sm:h-24 max-w-[260px] object-contain" data-id="element-837" data-mp="uzd1unyppq3u" />}
        </div> : null}

      <div className="p-6 sm:p-7" data-id="element-838" data-mp="2f4s1zk31mct">
        {/* Identity */}
        <div className="text-center" data-id="element-839" data-mp="2frij60wh5oix">
          {!school.logo && <span className="mx-auto mb-4 h-16 w-16 rounded-xl bg-light border border-gray-200 flex items-center justify-center" data-id="element-840" data-mp="2bpx181532k1v">
              <GraduationCapIcon className="w-8 h-8 text-navy" aria-hidden="true" data-id="element-841" data-mp="26phqlhc6rqnl" />
            </span>}
          <h2 className="text-2xl sm:text-[28px] font-extrabold text-navy leading-tight tracking-tight" data-id="element-842" data-mp="1wsd055bb590z">
            {school.name}
          </h2>
          {school.accreditation && <p className="flex items-start justify-center gap-1.5 text-xs text-gray-500 mt-2 leading-snug max-w-md mx-auto" data-id="element-843" data-mp="1rzjzasiymku6">
              <ShieldCheckIcon className="w-3.5 h-3.5 flex-shrink-0 mt-px text-gold-dark" aria-hidden="true" data-id="element-844" data-mp="1j18a8uxqrv3u" />
              {school.accreditation}
            </p>}
        </div>

        {/* Why this school is here. On the lead card the badge sits on this
            row, since it qualifies the match rather than the school's name. */}
        {(matchValue || isLead && school.highlight) && <div className="mt-5 rounded-lg border border-gray-200 bg-light px-4 py-3" data-id="element-845" data-mp="3e3lbk611bi0w">
            <div className="flex items-start justify-between gap-3" data-id="element-846" data-mp="2zegwgxpjdtx0">
              <p className="text-[11px] font-bold uppercase tracking-[0.12em] text-gray-500 pt-0.5" data-id="element-847" data-mp="34dtkvmw2391p">
                {matchLabel}
              </p>
              {isLead && school.highlight && <span className="flex-shrink-0 inline-flex items-center gap-1 rounded-full bg-white border border-gray-200 px-2.5 py-1 text-[11px] font-bold text-navy" data-id="element-848" data-mp="2ve6h4ufutr5h">
                  <SparklesIcon className="w-3.5 h-3.5 text-gold-dark" aria-hidden="true" data-id="element-849" data-mp="2giqsx61ygph0" />
                  {school.highlight}
                </span>}
            </div>
            {matchValue && <p className="text-base font-semibold text-navy mt-1" data-id="element-850" data-mp="jnb5wrqwfvzu">
                {matchValue}
              </p>}
          </div>}

        {/* Marketing copy — supporting, not decision-critical */}
        {school.about && <p className="mt-4 text-sm text-gray-600 leading-relaxed line-clamp-3" data-id="element-851" data-mp="2dwgaxnlqajmg">
            {school.about}
          </p>}

        {highlights.length > 0 && <ul className="mt-4 space-y-2" data-id="element-852" data-mp="293etb7u505z6">
            {highlights.map(item => <li key={item} className="flex items-center gap-2 text-sm text-gray-700" data-id="element-853" data-mp="2mutfton96paz">
                <CheckIcon className="w-4 h-4 flex-shrink-0 text-gold-dark" aria-hidden="true" data-id="element-854" data-mp="2wuk77hgioo6u" />
                <span className="truncate" data-id="element-855" data-mp="2rw8xd6q82dom">{item}</span>
              </li>)}
          </ul>}
      </div>

      {/* Action row */}
      <div className="border-t border-gray-200 px-6 sm:px-7 py-5" data-id="element-856" data-mp="xhya32p9zrky">
        {isLead && onConsentChange && <div className="mb-4" data-id="element-857" data-mp="2xkh8walirv80">
            <TCPADisclosure checked={consented} onChange={onConsentChange} error={consentError} checkboxClassName="mt-0.5 h-4 w-4 rounded border-gray-300 cursor-pointer flex-shrink-0 accent-[#1668E0]" textClassName="text-xs text-gray-500 leading-relaxed cursor-pointer" data-id="element-858" data-mp="2xlingp1quzuh">
              {disclosureHtml ? <span dangerouslySetInnerHTML={{
            __html: disclosureHtml
          }} data-id="element-859" data-mp="3gi0m7qf0g008" /> : fallbackDisclosure}
            </TCPADisclosure>
          </div>}

        <button type="button" onClick={onPrimary} disabled={isSubmitting} className="w-full inline-flex items-center justify-center gap-2 h-[52px] px-6 bg-primary hover:bg-primary-hover active:bg-secondary disabled:opacity-50 disabled:pointer-events-none text-white font-semibold rounded-full text-base leading-none shadow-ds-md transition-colors duration-150 ease-out focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary" data-id="element-860" data-mp="3q9wkp2l0ds4z">
          {isSubmitting && <LoaderIcon className="w-4 h-4 animate-spin" data-id="element-861" data-mp="88rbzhkvdiqz" />}
          {isLead ? isSubmitting ? 'Sending...' : 'I agree & continue' : <>
              Visit {school.name}
              <ArrowRightIcon className="w-4 h-4" aria-hidden="true" data-id="element-862" data-mp="10wn5kdf56ujj" />
            </>}
        </button>

        <button type="button" onClick={onSkip} disabled={isSubmitting} className="mt-3 w-full text-center text-sm text-gray-500 hover:text-gray-700 hover:underline py-1 transition-colors duration-200" data-id="element-863" data-mp="w0eubb42flhn">
          Skip this school
        </button>
      </div>
    </div>;
}